import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { FileText, Calendar, Printer, Download, X, Clock, CheckCircle, User } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { toast } from '../ui/use-toast';

const PatientRecords = ({ patientId, isOpen, onClose }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && patientId) {
      fetchPatientRecords();
    }
  }, [isOpen, patientId]);

  const fetchPatientRecords = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('get_patient_own_records.php');
      if (data.success) {
        // Records are already filtered by patient_id in the API
        // Sort by submitted date (newest first) - API already sorts, but ensure it
        const sortedRecords = data.submissions.sort((a, b) => 
          new Date(b.submitted_at) - new Date(a.submitted_at)
        );
        setRecords(sortedRecords);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load patient records',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching patient records:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load patient records',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (record) => {
    try {
      // Check if PDF URL exists in record data
      let pdfUrl = record.pdf_url;
      
      console.log('=== PRINT BUTTON CLICKED ===');
      console.log('Record ID:', record.id);
      console.log('PDF URL from record object:', pdfUrl);
      
      // If not in record object, fetch full record data
      if (!pdfUrl) {
        console.log('PDF URL not found in record object, fetching full record data...');
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${record.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
          console.log('PDF URL from API response:', pdfUrl);
        } else {
          console.log('Failed to fetch record data:', data);
        }
      }
      
      if (pdfUrl) {
        // Open PDF URL in new window/tab for printing
        // Convert relative URL to absolute if needed
        const absoluteUrl = pdfUrl.startsWith('http') 
          ? pdfUrl 
          : `${window.location.origin}${pdfUrl.startsWith('/') ? '' : '/'}${pdfUrl}`;
        
        console.log('Original PDF URL:', pdfUrl);
        console.log('Absolute PDF URL:', absoluteUrl);
        console.log('Window origin:', window.location.origin);
        console.log('Opening PDF in new window...');
        
        const printWindow = window.open(absoluteUrl, '_blank');
        
        if (printWindow) {
          console.log('PDF window opened successfully');
          // Wait for PDF to load, then trigger print dialog
          printWindow.onload = () => {
            console.log('PDF loaded in new window, triggering print dialog...');
            setTimeout(() => {
              printWindow.print();
            }, 500);
          };
        } else {
          console.error('Failed to open PDF window (pop-up blocked?)');
          toast({
            title: 'Error',
            description: 'Please allow pop-ups to print the PDF',
            variant: 'destructive'
          });
        }
      } else {
        console.warn('No PDF URL found');
        toast({
          title: 'PDF not available',
          description: 'PDF file is not available for this record',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to open PDF for printing',
        variant: 'destructive'
      });
    }
  };

  const handleDownload = (record) => {
    if (record.pdf_url) {
      // Convert relative URL to absolute if needed
      const absoluteUrl = record.pdf_url.startsWith('http') 
        ? record.pdf_url 
        : `${window.location.origin}${record.pdf_url.startsWith('/') ? '' : '/'}${record.pdf_url}`;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = absoluteUrl;
      link.download = `Patient-Record-${record.patient_name || 'Record'}-${record.appointment_reference_id || record.id}-${new Date(record.submitted_at).toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Download started',
        description: 'Your PDF is being downloaded'
      });
    } else {
      toast({
        title: 'PDF not available',
        description: 'PDF file is not available for this record',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto glass-effect border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl flex items-center">
              <FileText className="h-6 w-6 mr-2 text-blue-400" />
              Medical Records & History
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              View and download your submitted medical records and appointment PDFs
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Clock className="h-8 w-8 text-blue-400 animate-spin" />
              <span className="ml-3 text-gray-300">Loading records...</span>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-300 text-lg mb-2">No records found</p>
              <p className="text-gray-400 text-sm">
                Your submitted medical records and appointment PDFs will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {records.map((record) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="h-5 w-5 text-blue-400" />
                        <div>
                          <h3 className="text-white font-semibold text-lg">
                            {record.appointment_reference_id 
                              ? `Appointment: ${record.appointment_reference_id}`
                              : 'Medical Record'}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            Submitted on {formatDateTime(record.submitted_at)}
                          </p>
                        </div>
                      </div>
                      
                      {record.appointment_reference_id && (
                        <div className="flex items-center gap-2 text-sm text-gray-300 ml-8 mb-2">
                          <span>Appointment ID: {record.appointment_reference_id}</span>
                        </div>
                      )}
                      
                      {record.doctor_name && (
                        <div className="flex items-center gap-2 text-sm text-gray-300 ml-8">
                          <User className="h-4 w-4" />
                          <span>Submitted by: Dr. {record.doctor_name}</span>
                        </div>
                      )}
                      
                      {record.chief_complaint && (
                        <div className="mt-2 ml-8">
                          <p className="text-gray-400 text-sm">Chief Complaint:</p>
                          <p className="text-white text-sm">{record.chief_complaint}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePrint(record)}
                        disabled={!record.pdf_url}
                        className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(record)}
                        disabled={!record.pdf_url}
                        className="border-green-400 text-green-400 hover:bg-green-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                  
                  {record.pdf_url && (
                    <div className="mt-3 pt-3 border-t border-gray-700">
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <CheckCircle className="h-4 w-4 text-green-400" />
                        <span>PDF available</span>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </>
  );
};

export default PatientRecords;

